import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { formatCents, MESES } from '@/features/catalog/catalog';
import { EncabezadoSeccion, Importe } from '@/ui/Sistema';
import { computeItemKg } from '@/core/economics';
import { fetchWorkDetail, type WorkDetail } from '@/features/dashboard/dashboard';
import { fetchWorkForEdit, listParticipants, type Participant, type WorkLoaded } from './works';
import type { MobilityMode } from '@/core/economics';
import { fetchWorkInvoiceRecipients, type DestinatarioFactura } from '@/features/clients/clients';

/**
 * La ficha completa de un trabajo.
 *
 * Antes «Ver detalle» desplegaba un pedacito de la tarjeta con el reparto y
 * poco más. Todo lo que se había escrito al cargar el trabajo —cuántos kits se
 * gastaron, cuántas dosis, la observación al pie— sólo se podía volver a leer
 * entrando a **Editar**, que es exactamente el lugar donde no se quiere estar
 * para *consultar* algo: se entra a mirar y se sale habiendo tocado.
 *
 * Esta pantalla existe para contestar tres preguntas sin tener que calcular
 * nada: qué se hizo, cuánto salió, y cuánto le queda a cada uno.
 *
 * **La privacidad no se amplía para que la pantalla quede más linda.** Los
 * números por participante salen de `work_detail`, que es quien decide si
 * corresponde mandarlos con nombre o agregados sin nombre. Acá se dibuja lo que
 * llegó.
 *
 * Las secciones vacías no se dibujan: un trabajo sin movilidad no tiene por qué
 * mostrar un renglón que diga que no la tiene.
 *
 * **La excepción son los gastos del trabajo**, que se muestran incluso vacíos.
 * Con el resto alcanza con mirar: si no hay movilidad, el importe ya lo dice.
 * Los gastos no: no aparecer por no haberse anotado y no aparecer por no
 * dibujarse se ven idénticos, y se sale de la pantalla sin saber si el dato se
 * perdió o nunca se escribió.
 */

const MODOS: Record<MobilityMode, string> = {
  none: 'No se cobra',
  per_km_fuel: 'Por kilómetro, según el precio de la nafta',
  per_km_fixed: 'Por kilómetro, con tarifa fija',
  flat: 'Monto fijo por viaje',
  manual: 'Importe cargado a mano',
};

export function WorkDetailPage({
  workId,
  onVolver,
  onEditar,
}: {
  workId: string;
  onVolver: () => void;
  onEditar: (id: string) => void;
}) {
  const [w, setW] = useState<WorkLoaded | null>(null);
  const [eco, setEco] = useState<WorkDetail | null>(null);
  const [gente, setGente] = useState<Participant[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [destinatarios, setDestinatarios] = useState<DestinatarioFactura[]>([]);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        // El detalle económico va por su propio camino a propósito: es el que
        // aplica las reglas de privacidad, y no tiene sentido replicarlas acá.
        const [cargado, economico] = await Promise.all([
          fetchWorkForEdit(workId),
          fetchWorkDetail(workId),
        ]);
        if (cancelado) return;
        setW(cargado);
        setEco(economico);
        const p = await listParticipants(cargado.organization_id);
        if (!cancelado) setGente(p);
        try {
          const dest = await fetchWorkInvoiceRecipients(workId);
          if (!cancelado) setDestinatarios(dest);
        } catch {
          // Sin reparto de factura, se le emitió entera al cliente.
        }
      } catch (err) {
        if (!cancelado) setError(mensajeDeError(err));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [workId]);

  const nombre = (id: string) => gente.find((g) => g.id === id)?.display_name ?? '—';

  if (error) {
    return (
      <Marco onVolver={onVolver}>
        <p className="text-sm text-[var(--peligro)]">{error}</p>
      </Marco>
    );
  }
  if (!w || !eco) {
    return (
      <Marco onVolver={onVolver}>
        <p className="text-sm text-[var(--tinta-suave)]">Cargando…</p>
      </Marco>
    );
  }

  const mesFacturacion = `${MESES[w.billing_month - 1]} ${w.billing_year}`;
  const mismoMes =
    w.billing_month === Number(w.work_date.slice(5, 7)) &&
    w.billing_year === Number(w.work_date.slice(0, 4));
  const precio = w.steer_price_cents;

  return (
    <Marco onVolver={onVolver}>
      {/* --- Encabezado ------------------------------------------------- */}
      <header className="flex flex-col gap-1" data-testid="detalle-encabezado">
        <h1 className="text-xl font-semibold">{w.client_name}</h1>
        {w.establishment_name && (
          <p className="text-sm text-[var(--tinta-suave)]">{w.establishment_name}</p>
        )}
        <p className="text-sm text-[var(--tinta-suave)]">
          {new Date(`${w.work_date}T00:00:00`).toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
          {/* El mes de facturación sólo se nombra cuando NO es el del trabajo.
              Repetirlo siempre era ruido; callarlo siempre escondía el caso
              que de verdad importa, que es cuando difieren. */}
          {!mismoMes && ` · se factura en ${mesFacturacion}`}
        </p>
        <div className="mt-2 flex items-baseline justify-between gap-3">
          <Importe className="text-2xl" pendiente={eco.price_pending} data-testid="detalle-total">
            {formatCents(eco.total_cents)}
          </Importe>
          {/* Los mismos dos estados que en la lista, y con las mismas marcas.
              Antes acá había un cartel con otro texto para decir lo mismo, y
              volver de la ficha a la lista obligaba a traducir. */}
          <span className="flex shrink-0 gap-3 text-sm" data-testid="detalle-estado">
            <span
              className={
                w.is_billed ? 'font-medium text-[var(--accion)]' : 'text-[var(--tinta-tenue)]'
              }
            >
              {w.is_billed ? '✓' : '○'} Facturado
            </span>
            <span
              className={
                w.is_paid ? 'font-medium text-[var(--exito)]' : 'text-[var(--tinta-tenue)]'
              }
            >
              {w.is_paid ? '✓' : '○'} Cobrado
            </span>
          </span>
        </div>
        {eco.price_pending && (
          <p className="text-sm text-[var(--aviso)]" data-testid="detalle-pendiente">
            Falta el precio del novillo del período: los kilos están, los pesos todavía no.
          </p>
        )}
        <p className="text-sm text-[var(--tinta-suave)]" data-testid="detalle-facturador">
          Factura {nombre(w.biller_participant_id)}
        </p>
      </header>

      {/* --- Honorarios --------------------------------------------------- */}
      {w.items.length > 0 && (
        <Seccion titulo="Honorarios" testId="detalle-honorarios">
          <ul className="flex flex-col divide-y divide-[var(--linea)]">
            {w.items.map((it, i) => {
              const kg = computeItemKg({
                serviceName: it.service_name,
                calculation: it.calculation,
                kgPerUnit: it.kg_per_unit,
                quantity: it.quantity,
              });
              return (
                <li key={`${it.organization_service_id ?? 'x'}-${i}`} className="py-2">
                  <div className="flex justify-between gap-3">
                    <span className="min-w-0 flex-1 truncate">{it.service_name}</span>
                    <span className="shrink-0 tabular-nums">
                      {precio === null ? `${kg} kg` : formatCents(Math.round(kg * precio))}
                    </span>
                  </div>
                  <p className="text-xs text-[var(--tinta-suave)]">
                    {it.calculation === 'per_job'
                      ? `Fijo por trabajo · ${it.kg_per_unit} kg`
                      : `${it.quantity} × ${it.kg_per_unit} kg`}
                    {` · ${kg} kg en total`}
                  </p>
                </li>
              );
            })}
          </ul>
        </Seccion>
      )}

      {/* --- Movilidad ---------------------------------------------------- */}
      {w.mobility && w.mobility.mode !== 'none' && (
        <Seccion titulo="Movilidad" testId="detalle-movilidad">
          <Renglon rotulo="Método" valor={MODOS[w.mobility.mode]} />
          {w.mobility.km !== null && <Renglon rotulo="Kilómetros" valor={String(w.mobility.km)} />}
          {w.mobility.rate_cents !== null && (
            <Renglon
              rotulo={
                w.mobility.mode === 'per_km_fuel' ? 'Precio de la nafta' : 'Tarifa por kilómetro'
              }
              valor={formatCents(w.mobility.rate_cents)}
            />
          )}
          <Renglon rotulo="Importe" valor={formatCents(eco.mobility_cents)} />
          {w.mobility.allocations.length > 0 && (
            // El porcentaje se muestra siempre que no sea el total, y no
            // «cuando hay más de uno». La diferencia importa por RLS: a un
            // miembro común la base le manda ÚNICAMENTE su propia fila, así que
            // una sola fila no significa que sea el único beneficiario. Contar
            // filas para decidir la redacción haría que quien cobra el 40 %
            // leyera «La cobra» y entendiera que le toca todo.
            <Renglon
              rotulo="Movilidad para"
              valor={w.mobility.allocations
                .map((a) =>
                  a.percentage === 100
                    ? nombre(a.participant_id)
                    : `${nombre(a.participant_id)} ${a.percentage} %`,
                )
                .join(' · ')}
            />
          )}
        </Seccion>
      )}

      {/* --- A nombre de quién se factura --------------------------------- */}
      {/* Es OTRA cosa que el reparto de abajo, y conviene que se vea separado:
          uno dice a quién se le cobra afuera, el otro cuánto gana cada uno
          adentro. Confundirlos es lo que llevaba a facturarle el 100 % a una
          sola persona de un campo que era de dos. */}
      {destinatarios.length > 0 && (
        <Seccion titulo="Facturar a" testId="detalle-destinatarios">
          <ul className="flex flex-col divide-y divide-[var(--linea)]">
            {destinatarios.map((d, i) => (
              <li key={i} className="flex justify-between gap-3 py-2">
                <span className="min-w-0 flex-1 truncate">
                  {d.name}
                  <span className="ml-2 text-xs text-[var(--tinta-suave)]">{d.percentage} %</span>
                </span>
                <span className="shrink-0 tabular-nums">
                  {formatCents(
                    eco.total_cents === null
                      ? null
                      : Math.round((eco.total_cents * d.percentage) / 100),
                  )}
                </span>
              </li>
            ))}
          </ul>
        </Seccion>
      )}

      {/* --- Cuánto le queda a cada uno ----------------------------------- */}
      <Seccion titulo="Cuánto le queda a cada uno" testId="detalle-reparto">
        {/* La movilidad se muestra aparte de los honorarios porque es de otra
            naturaleza: no se reparte por el mismo criterio y muchas veces la
            cobra alguien que no participó del reparto. Sumarlas en un solo
            número obliga a saber la fórmula para poder leerlo. */}
        {eco.splits ? (
          <ul className="flex flex-col divide-y divide-[var(--linea)]">
            {eco.splits.map((s) => (
              <li key={s.participant_id} className="flex justify-between gap-3 py-2">
                <span className="min-w-0 flex-1 truncate">
                  {s.display_name}
                  <span className="ml-2 text-xs text-[var(--tinta-suave)]">{s.percentage} %</span>
                </span>
                <span className="shrink-0 tabular-nums">{formatCents(s.amount_cents)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="flex flex-col divide-y divide-[var(--linea)]">
            {eco.my_share && eco.my_share.percentage !== null && (
              <li className="flex justify-between gap-3 py-2" data-testid="detalle-mi-parte">
                <span>
                  Lo tuyo
                  <span className="ml-2 text-xs text-[var(--tinta-suave)]">
                    {eco.my_share.percentage} %
                  </span>
                </span>
                <span className="tabular-nums">{formatCents(eco.my_share.total_cents)}</span>
              </li>
            )}
            {eco.others && (
              // Sin nombres ni montos individuales: lo que le corresponde a
              // otro no es información de quien mira (D1).
              <li className="flex justify-between gap-3 py-2 opacity-70">
                <span>
                  Otros participantes
                  <span className="ml-2 text-xs">{eco.others.percentage} %</span>
                </span>
                <span className="tabular-nums">{formatCents(eco.others.honoraria_cents)}</span>
              </li>
            )}
          </ul>
        )}

        {w.mobility &&
          w.mobility.mode !== 'none' &&
          w.mobility.allocations.map((a) => (
            <p
              key={a.participant_id}
              className="mt-2 flex justify-between gap-3 border-t border-[var(--linea)] pt-2 text-sm"
            >
              <span className="opacity-70">Movilidad de {nombre(a.participant_id)}</span>
              <span className="tabular-nums">
                {formatCents(
                  eco.mobility_cents === null
                    ? null
                    : Math.round((eco.mobility_cents * a.percentage) / 100),
                )}
              </span>
            </p>
          ))}
      </Seccion>

      {/* --- Lo que se gastó en el campo ---------------------------------- */}
      {/* La sección se muestra SIEMPRE, incluso vacía. Cuando desaparecía al no
          haber texto, la pantalla contestaba lo mismo —nada— en dos casos muy
          distintos: que no se hubiera anotado ningún gasto, y que la pantalla
          no lo estuviera mostrando. Se buscaba el gasto de una cesárea y no
          había forma de saber cuál de los dos era. El renglón vacío lo dice. */}
      <Seccion titulo="Gastos del trabajo" testId="detalle-observaciones">
        {w.description ? (
          /* `whitespace-pre-wrap`: se escribe en renglones —«1,3 kit», «13
             dosis», «0,1 vainas»— y aplastarlo en un párrafo corrido lo vuelve
             ilegible justo cuando se lo está buscando. */
          <p className="whitespace-pre-wrap text-sm">{w.description}</p>
        ) : (
          <p className="text-sm text-[var(--tinta-suave)]">
            No se anotó ningún gasto en este trabajo.
          </p>
        )}
      </Seccion>

      {/* --- Administración ------------------------------------------------ */}
      <Seccion titulo="Administración" testId="detalle-administracion">
        <Renglon rotulo="Facturación" valor={w.is_billed ? 'Facturado' : 'Sin facturar'} />
        <Renglon rotulo="Cobro" valor={w.is_paid ? 'Cobrado' : 'Sin cobrar'} />
        <Renglon rotulo="Mes de facturación" valor={mesFacturacion} />
        {w.is_locked && (
          <Renglon
            rotulo="Cierre"
            valor="Pertenece a un período cerrado. Para corregirlo hay que reabrirlo desde Liquidación."
          />
        )}
        {w.price_frozen && <Renglon rotulo="Precios" valor="Congelados por el cierre" />}
      </Seccion>

      {!w.is_locked && (
        <button
          type="button"
          onClick={() => onEditar(w.id)}
          className="min-h-12 w-full superficie text-sm font-medium"
          data-testid="detalle-editar"
        >
          Editar este trabajo
        </button>
      )}
    </Marco>
  );
}

function Marco({ children, onVolver }: { children: React.ReactNode; onVolver: () => void }) {
  return (
    <div className="flex flex-col gap-6 p-4" data-testid="work-detail">
      <button
        type="button"
        onClick={onVolver}
        className="-ml-1 min-h-11 self-start px-1 text-sm text-[var(--tinta-suave)]"
        data-testid="detalle-volver"
      >
        ‹ Volver a Trabajos
      </button>
      {children}
    </div>
  );
}

/**
 * Una sección de la ficha.
 *
 * Separadas por aire y por tipografía, no metidas en cajas. Antes eran seis
 * rectángulos apilados y la ficha se leía como seis pantallas pegadas: cada
 * borde decía «acá empieza otra cosa» con el mismo énfasis que el título, así
 * que ninguno de los dos se leía.
 */
function Seccion({
  titulo,
  testId,
  children,
}: {
  titulo: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2" data-testid={testId}>
      <EncabezadoSeccion titulo={titulo} />
      {children}
    </section>
  );
}

function Renglon({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <p className="flex justify-between gap-3 py-1 text-sm">
      <span className="shrink-0 opacity-70">{rotulo}</span>
      <span className="text-right">{valor}</span>
    </p>
  );
}
